import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import EditorLayout from '../../../../components/Layouts/EditorLayout';
import SMCTextInput from '../../../../components/Simple/SMCTextInput';
import SMCButton from '../../../../components/Simple/SMCButton';
import RowLayout from '../../../../components/Layouts/RowLayout';
import {UserGetSchema} from '../../../../api/DBSchema/User';
import SMCLabel from '../../../../components/Simple/SMCLabel';
import {SMCSelect, SMCOption} from '../../../../components/Simple/SMCSelect';
import {SMCCheckbox} from '../../../../components/Simple/SMCCheckbox';
import ChangePasswordModal from '../../../PopupPages/ChangePasswordModal';
import {useBoolean} from '../../../../utils/hooks';
import Api from "../../../../api/SMPFetchHelper";
import {useSMCErrorHandler} from "../../../../utils/errorUtils";

const pageSchema = UserGetSchema.pages.general;
const pageId = pageSchema.pageId;

function UserGeneral({
    userObject,
    setUserData,
    availableRoles,
    minimumPortalPasswordLength,
    maximumPortalPasswordLength
}) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();

    const pageData  = userObject[pageId];
    const userId    = userObject[UserGetSchema.id];
    const setPageValue = useCallback((paramName, value) => {
        setUserData([pageId, paramName], value);
    }, [setUserData]);

    const firstName        = pageData[pageSchema.firstName];
    const lastName         = pageData[pageSchema.lastName];
    const title            = pageData[pageSchema.title];
    const organization     = pageData[pageSchema.organization];
    const email            = pageData[pageSchema.email];
    const additionalEmails = pageData[pageSchema.additionalEmails];
    const role             = pageData[pageSchema.role];
    const comment          = pageData[pageSchema.comment];
    const isBypassRadiusAuth = pageData[pageSchema.isBypassRadiusAuth];

    const [isChangePasswordWindowOpen, openChangePasswordWindow, closeChangePasswordWindow] = useBoolean(false);

    return (
        <EditorLayout>
            <SMCTextInput
                value={firstName}
                onChange={e => setPageValue(pageSchema.firstName, e.target.value)}
                label={t('SMP.USERS.TABLE.PAGES.FIRST_NAME')}
                required
            />
            <SMCTextInput
                value={lastName}
                onChange={e => setPageValue(pageSchema.lastName, e.target.value)}
                label={t('SMP.USERS.TABLE.PAGES.LAST_NAME')}
                required
            />
            <SMCTextInput
                value={title}
                onChange={e => setPageValue(pageSchema.title, e.target.value)}
                label={t('SMP.USERS.TABLE.PAGES.TITLE')}
            />
            <SMCTextInput
                value={organization}
                onChange={e => setPageValue(pageSchema.organization, e.target.value)}
                label={t('SMP.USERS.TABLE.PAGES.COMPANY')}
            />
            <SMCTextInput
                value={email}
                onChange={e => setPageValue(pageSchema.email, e.target.value)}
                label={t('SMP.USERS.TABLE.PAGES.EMAIL')}
                required
            />
            <SMCTextInput
                value={additionalEmails}
                onChange={e => setPageValue(pageSchema.additionalEmails, e.target.value)}
                label={t('SMP.USERS.TABLE.PAGES.ADDITIONAL_EMAILS')}
                tooltip={t('SMP.USERS.TABLE.PAGES.ADDITIONAL_EMAILS.TOOLTIP')}
            />
            <SMCSelect
                value={role}
                label={t("SMP.USERS.TABLE.PAGES.ROLE")}
                onChange={e => setPageValue(pageSchema.role, e.target.value)}
            >
                {availableRoles && availableRoles.map(role => <SMCOption key={role} value={role}>{role}</SMCOption>)}
            </SMCSelect>
            <RowLayout>
                <SMCLabel useContextWidth inline>{t('SMP.USERS.TABLE.PAGES.PASSWORD')}</SMCLabel>
                <SMCButton onClick={openChangePasswordWindow}>{t('SMC_GEN_CHANGE')}</SMCButton>
            </RowLayout>
            <SMCTextInput
                label={t("SMC_USER_COMMENT")}
                value={comment}
                onChange={e => setPageValue(pageSchema.comment, e.target.value)}
            />
            <SMCCheckbox
                checked={isBypassRadiusAuth}
                onChange={e => setPageValue(pageSchema.isBypassRadiusAuth, e.target.checked)}
                label={t('SMC_USER_IS_BYPASS_RADIUS_AUTH')}
                labelPosition="start"
            />
            {/* Popup windows */}
            <ChangePasswordModal
                isOpen={isChangePasswordWindowOpen}
                onRequestClose={closeChangePasswordWindow}
                minimumLength={minimumPortalPasswordLength}
                maximumLength={maximumPortalPasswordLength}
                useMinimumLength={true}
                useMaximumLength={true}
                onFinish={password => {
                    Api.validateUserPasswordField(userId, password)
                        .then(() => {
                            setPageValue(pageSchema.password, password);
                            closeChangePasswordWindow();
                        })
                        .catch(e => {
                            alert(getErrorMessage(e));
                        });
                }}
            />
        </EditorLayout>
    )
}

export default UserGeneral;